import React, { useCallback, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { performGetRequest } from '@atlassian/jira-fetch';
import { useIntl } from '@atlassian/jira-intl';
import type { Option, Options } from '@atlassian/jira-issue-field-select-base';
import { SearchableSelect } from '@atlassian/jira-searchable-select';
import messages from './messages';
import type { Props } from './types';
import {
	transformIssueSuggestionItems,
	stripUrlToIssueKey,
	getCurrentHostPattern,
	stripQueryValueToIssueKey,
} from './utils';

const IssueLinksSearchEdit = (props: Props) => {
	const { formatMessage } = useIntl();
	const [invalidSelections, setInValidSelections] = useState<unknown>([]);
	const [isInvalidState, setIsInvalidState] = useState(false);

	const {
		fieldId,
		onFocus,
		onBlur,
		value,
		options,
		onChange,
		autoCompleteUrl,
		loadingMessage,
		getSuggestions,
		useTransformSuggestions,
		noOptionsMessage,
		fetchSuggestionsOnMount,
		isInvalid,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		isDisabled,
	} = props;

	const fetchSuggestions = useCallback(
		async (query: string) => {
			const response = await performGetRequest(`${autoCompleteUrl}${query}`);
			return response;
		},
		[autoCompleteUrl],
	);

	/** returns the update invalid selection list that only contains values still present in the user selections */
	const updateInvalidSelections = useCallback(
		(selectionValues: Option[]): void => {
			const issueKeyValues = selectionValues ? selectionValues.map((v) => v.value) : [];
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const updateSelections = invalidSelections.length
				? // @ts-expect-error - TS2571 - Object is of type 'unknown'. | TS7006 - Parameter 'i' implicitly has an 'any' type.
					invalidSelections.filter((i) => issueKeyValues.includes(i))
				: [];
			if (updateSelections.length) {
				setIsInvalidState(true);
			} else {
				setIsInvalidState(false);
			}
			setInValidSelections(updateSelections);
		},
		[invalidSelections],
	);

	const onChangeSelectedValues = useCallback(
		(values: Option[], suggestions?: Options): void => {
			const currentHost = getCurrentHostPattern();
			updateInvalidSelections(values);
			const newIssueLinks =
				values !== null && values !== undefined
					? values.map((issueLink) => {
							const strippedIssueKey = stripUrlToIssueKey(currentHost, issueLink.value.toString());

							return {
								...issueLink,
								iconUrl:
									issueLink.__isNew__ === true && suggestions && suggestions[0].options
										? suggestions[0].options.find((option) => option.value === issueLink.value)
												?.iconUrl
										: issueLink.iconUrl,
								label: strippedIssueKey,
								value: strippedIssueKey,
							};
						})
					: values;
			onChange(newIssueLinks);
		},
		[onChange, updateInvalidSelections],
	);

	return (
		<FlexRow>
			<Box xcss={searchWrapperStyles}>
				<SearchableSelect
					fieldId={fieldId}
					getSuggestions={getSuggestions !== undefined ? getSuggestions : fetchSuggestions}
					onChange={onChangeSelectedValues}
					isMulti
					value={value}
					options={options}
					onBlur={onBlur}
					onFocus={onFocus}
					onCloseMenuOnScroll={onCloseMenuOnScroll}
					isDropdownMenuFixedAndLayered={isDropdownMenuFixedAndLayered}
					isDisabled={isDisabled}
					placeholder={formatMessage(messages.placeholder)}
					getTransformedSuggestions={
						useTransformSuggestions !== undefined
							? useTransformSuggestions
							: transformIssueSuggestionItems
					}
					canCreateNewItem
					loadingMessage={loadingMessage}
					noOptionsMessage={noOptionsMessage}
					onChangeQueryValue={stripQueryValueToIssueKey}
					isInvalid={isInvalid === true || isInvalidState}
					fetchSuggestionsOnMount={fetchSuggestionsOnMount}
					formatCreateLabel={formatMessage(messages.createNewIssueLink)}
					ariaLabel={formatMessage(messages.placeholder)}
				/>
			</Box>
		</FlexRow>
	);
};

export default IssueLinksSearchEdit;

const searchWrapperStyles = xcss({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexRow = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	flex: 1,
});
